// [목적] /login 데스크톱 Split Layout — 좌 50% BrandVisualPanel + 우 50% 폼 (디자인 원본 1:1 포팅)
// [이유] 2026-05-16 reauth-redesign — docs/플리오랩/gangwonfolio-web.html LoginScreenA 의 AuthShell 데스크톱 레이아웃 적용.
//         이전 warm/earthy 토큰 + 마스코트 로고 + 워드마크 h5 + 둘러보기 CTA + 약관 줄바꿈 구성 폐기.
//         새 구성:
//           좌 50%: BrandVisualPanel (블루 그라데이션 + 산 SVG + 3-zone)
//           우 50%: max-w 380 폼 카드 — 타이틀 "강원을 그리다"(26px) + 부카피 + OAuthButtonStack + 약관(중앙, 한 줄)
//         "둘러보기" CTA 및 카드 boxShadow 제거 (디자인 원본 미포함).
// [사이드 이팩트]
//   - 루트 컨테이너: grid 1fr 1fr (이전 flex 1+1 동등하나 grid 가 디자인 원본과 일치).
//   - 우측 패널 폼은 alignItems/justifyContent center — 380 폭 카드 중앙 정렬.
//   - OAuthButtonStack: disabledProviders=[] (Apple 활성) — 디자인 원본의 4종 모두 활성 반영.
//     ※ Apple OAuth BE 미구현이면 사용자 협의 후 disabledProviders=['apple'] 재적용.
//   - 약관 링크: role="link" + tabIndex=0 + onKeyDown Enter — WCAG 2.1 AA 키보드 경로 보장.
//   - 포커스 링: brandBlue (warm 시절 brandGold 폐기).
//   - LoginPage 의 onAnonBrowse prop 은 미사용으로 시그니처에서 제거 (호환 영향: LoginPage 갱신 필수).
// [수정 시 고려사항]
//   - viewport 분기는 LoginPage 가 useIsDesktop 으로 처리 — 본 컴포넌트는 항상 데스크톱 가정.
//   - Tab 순서: OAuth 버튼 4종 → 이용약관 → 개인정보 처리방침.
//   - AUTH_COLORS / AUTH_SPACING / AUTH_BORDER 토큰 경유 필수.
//   - desktopCardMaxWidth(380) 조정 시 tokens.ts 만 수정.

import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { BrandVisualPanel } from './BrandVisualPanel.js';
import { OAuthButtonStack, type OAuthProvider } from './OAuthButtonStack.js';
import { AUTH_BORDER, AUTH_COLORS, AUTH_SPACING } from './tokens.js';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LoginDesktopLayoutProps {
  /** OAuth 버튼 클릭 핸들러 — LoginPage 에서 주입. OAuth 흐름 로직은 LoginPage 에 보존(R-8). */
  onProviderClick: (provider: OAuthProvider) => void;
}

// ─── 컴포넌트 ────────────────────────────────────────────────────────────────

/**
 * [목적] /login 데스크톱 Split Layout (viewport >= 1024px)
 * [이유] 디자인 원본 LoginScreenA — 좌 BrandVisualPanel · 우 폼 카드 (max-w 380, 중앙)
 * [사이드 이팩트] grid 1fr 1fr 로 균등 분할. 우측 폼은 카드 boxShadow 없음 (디자인 원본 반영)
 * [수정 시 고려사항] Apple disabled 처리는 LoginPage 가 결정 — 본 컴포넌트는 노출만 책임
 */
export function LoginDesktopLayout({ onProviderClick }: LoginDesktopLayoutProps): JSX.Element {
  return (
    <Box
      component="main"
      aria-label="강원캔버스 로그인"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        minHeight: '100vh',
        width: '100%',
        bgcolor: AUTH_COLORS.surface,
      }}
    >
      {/* ── 좌측 50% — 브랜드 비주얼 패널 ────────────────────────────────── */}
      <BrandVisualPanel />

      {/* ── 우측 50% — 로그인 폼 패널 ──────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: AUTH_SPACING.desktopPanelPx,
          py: '48px',
          overflowY: 'auto',
          bgcolor: AUTH_COLORS.surface0,
        }}
      >
        {/* 폼 카드 — max-w 380 (디자인 원본 1:1) */}
        <Box
          sx={{
            width: '100%',
            maxWidth: AUTH_SPACING.desktopCardMaxWidth,
          }}
        >
          {/* 1. 타이틀 + 부카피 */}
          <Box sx={{ mb: AUTH_SPACING.desktopHeaderToStackMb }}>
            <Typography
              component="h1"
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: AUTH_COLORS.textHigh,
                letterSpacing: '-0.6px',
                lineHeight: 1.2,
                mb: AUTH_SPACING.headlineToSubMb,
              }}
            >
              강원을 그리다
            </Typography>
            <Typography
              sx={{
                fontSize: 13.5,
                color: AUTH_COLORS.textMid,
                lineHeight: 1.6,
                letterSpacing: '-0.1px',
              }}
            >
              로그인하고 나만의 강원 여행 폴리오를 시작해보세요.
            </Typography>
          </Box>

          {/* 2. OAuth 버튼 스택 — [수정: 디자인 개편] Apple은 OAuthButtonStack 자체에서 완전히 제거되어 3종(카카오/구글/네이버)만 나온다. */}
          <OAuthButtonStack onProviderClick={onProviderClick} fullWidth />

          {/* [수정: 디자인 개편] "로그인 없이 먼저 만들어보기" — /chat 은 비로그인 사용자도 입력까지 체험할 수 있고(실제 생성 시점에만 로그인 요구), 앱 가입 전에 먼저 경험하게 해준다. */}
          <Box
            component={RouterLink}
            to="/chat"
            aria-label="로그인 없이 먼저 만들어보기"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '48px',
              mt: AUTH_SPACING.stackGap,
              border: `1.5px solid ${AUTH_COLORS.dividerBase}`,
              borderRadius: AUTH_BORDER.buttonRadius,
              color: AUTH_COLORS.textHigh,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
              },
            }}
          >
            로그인 없이 먼저 만들어보기
          </Box>

          {/* 3. 약관 안내 — 중앙, 한 줄 */}
          <Typography
            sx={{
              textAlign: 'center',
              mt: '24px',
              fontSize: 11,
              color: AUTH_COLORS.textLow,
              lineHeight: 1.6,
            }}
          >
            가입 시{' '}
            {/* [수정: QA-P2-18/P3-01] span role="link" 수동 구현 대신 RouterLink 로 교체 — 실제 <a> 시맨틱을 얻어
                탭 순서/키보드 활성화(Enter)가 브라우저 기본 동작으로 보장되고, 브라우저 컨텍스트 메뉴(새 탭에서 열기 등)도 정상 동작한다. */}
            <Box
              component={RouterLink}
              to="/settings/tos"
              aria-label="이용약관 보기"
              sx={{
                color: AUTH_COLORS.dividerLink,
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: AUTH_COLORS.textHigh },
                '&:focus-visible': {
                  outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                  outlineOffset: AUTH_BORDER.focusRingOffset,
                  borderRadius: '2px',
                },
              }}
            >
              이용약관
            </Box>{' '}
            및{' '}
            <Box
              component={RouterLink}
              to="/settings/privacy"
              aria-label="개인정보 처리방침 보기"
              sx={{
                color: AUTH_COLORS.dividerLink,
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': { color: AUTH_COLORS.textHigh },
                '&:focus-visible': {
                  outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                  outlineOffset: AUTH_BORDER.focusRingOffset,
                  borderRadius: '2px',
                },
              }}
            >
              개인정보 처리방침
            </Box>
            에 동의한 것으로 간주됩니다
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
